import React, { useState, useEffect, useRef } from 'react';
import { checkIn, checkOut, onMyAttendanceChange, hoursWorkedForDisplay, toDate } from '../services/attendanceDb';
import { 
  MapPin, 
  CheckCircle2, 
  PartyPopper, 
  ClipboardList, 
  CalendarDays, 
  Bell, 
  Building2, 
  Clock, 
  ShieldCheck, 
  Briefcase,
  UserCheck,
} from 'lucide-react';
import {
  normalizeHeadDepartmentsFromUser,
  leaveNeedsDepartmentHeadAction,
} from '../services/userDb';

const formatDuration = (hours) => {
  if (!hours || hours <= 0) return '0s';
  const totalSeconds = Math.floor(hours * 3600);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

export default function MyDashboard({ currentUser, leaves, employees = [], onNavigate }) {
  const [todaysAttendance, setTodaysAttendance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(new Date());
  const actionLock = useRef(false);

  const myLeaves = (leaves || []).filter(l => l.eEmail === currentUser.email || l.emp === currentUser.name);
  const pendingLeaves = myLeaves.filter(
    (l) => l.status === 'pending' || l.status === 'pending_head'
  ).length;
  const approvedLeaves = myLeaves.filter(l => l.status === 'approved').reduce((acc, l) => acc + (l.days || 0), 0);

  const headDepartments = normalizeHeadDepartmentsFromUser(currentUser);
  const teamApprovalLeaves = (leaves || []).filter((l) =>
    leaveNeedsDepartmentHeadAction(l, currentUser, employees)
  );
  const teamPendingCount = teamApprovalLeaves.length;
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();

  useEffect(() => {
    // Live clock
    const clockTimer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(clockTimer);
  }, []);

  useEffect(() => {
    if (!currentUser?.email) return;
    const unsub = onMyAttendanceChange(currentUser.email, (data) => {
      const todayRecord = data.find(h => h.date === today);
      setTodaysAttendance(todayRecord || null);
    });
    return unsub;
  }, [currentUser.email, today]);

  const handleCheckIn = async () => {
    if (actionLock.current) return;
    actionLock.current = true;
    setLoading(true);
    const tempAttendance = {
      checkIn: new Date().toISOString(),
      date: today,
      status: 'IN',
      optimistic: true,
    };
    setTodaysAttendance(tempAttendance);
    try {
      await checkIn(currentUser.email);
    } catch (e) {
      console.error(e);
      setTodaysAttendance(null);
    } finally {
      actionLock.current = false;
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!todaysAttendance || actionLock.current) return;
    actionLock.current = true;
    setLoading(true);
    const checkInTime = toDate(todaysAttendance.checkIn);
    const checkoutDate = new Date();
    const totalHours = checkInTime
      ? (checkoutDate - checkInTime) / (1000 * 60 * 60)
      : 0;
    const tempAttendance = {
      ...todaysAttendance,
      checkOut: checkoutDate.toISOString(),
      status: 'OUT',
      totalHours,
      optimistic: true,
    };
    setTodaysAttendance(tempAttendance);
    try {
      await checkOut(currentUser.email);
    } catch (e) {
      console.error(e);
    } finally {
      actionLock.current = false;
      setLoading(false);
    }
  };

  const formatTime = (isoString) => {
    const d = toDate(isoString);
    if (!d) return '--:--';
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const getElapsed = () => {
    if (!todaysAttendance?.checkIn) return null;
    const checkInTime = toDate(todaysAttendance.checkIn);
    if (!checkInTime) return null;
    if (todaysAttendance.checkOut) {
      const co = toDate(todaysAttendance.checkOut);
      if (!co) return '00:00:00';
      const diff = Math.floor((co - checkInTime) / 1000);
      if (isNaN(diff) || diff < 0) return '00:00:00';
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
    }
    const hours = hoursWorkedForDisplay(
      todaysAttendance.checkIn,
      null,
      todaysAttendance.date || today,
      todaysAttendance.status
    );
    const totalSeconds = Math.max(0, Math.floor(hours * 3600));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  };

  const isCheckedIn = !!todaysAttendance?.checkIn;
  const isCheckedOut = !!todaysAttendance?.checkOut;
  const elapsed = getElapsed();

  return (
    <div style={{ 
      padding: '16px', 
      maxWidth: '800px', 
      margin: '0 auto',
      fontFamily: 'var(--font)'
    }}>

      {/* Greeting */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text)', marginBottom: '4px' }}>
          Hey, {currentUser.name?.split(' ')[0] || 'there'} 👋
        </h1>
        <p style={{ color: 'var(--text2)', fontSize: '13px' }}>
          {now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Department head: highlighted team leave queue */}
      {headDepartments.length > 0 && (
        <button
          type="button"
          onClick={() => onNavigate && onNavigate('team-leaves')}
          style={{
            width: '100%',
            textAlign: 'left',
            marginBottom: '20px',
            padding: '16px 18px',
            borderRadius: '14px',
            border: teamPendingCount > 0 ? '2px solid #f59e0b' : '1px solid var(--border)',
            background:
              teamPendingCount > 0
                ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 55%, #fff 100%)'
                : 'var(--surface)',
            boxShadow:
              teamPendingCount > 0 ? '0 6px 24px rgba(245, 158, 11, 0.22)' : '0 1px 3px rgba(0,0,0,0.06)',
            cursor: 'pointer',
            fontFamily: 'inherit',
            animation: teamPendingCount > 0 ? 'headPulse 2.2s ease-in-out infinite' : undefined,
          }}
        >
          <style>{`
            @keyframes headPulse {
              0%, 100% { box-shadow: 0 6px 24px rgba(245, 158, 11, 0.22); }
              50% { box-shadow: 0 8px 28px rgba(245, 158, 11, 0.38); }
            }
          `}</style>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: teamPendingCount > 0 ? 'rgba(245, 158, 11, 0.25)' : 'var(--accent-bg)',
                  color: teamPendingCount > 0 ? '#b45309' : 'var(--accent)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <UserCheck size={22} strokeWidth={2.25} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text)' }}>
                  Team leave approvals
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: '2px' }}>
                  Head of: {headDepartments.join(', ')}
                  {teamPendingCount > 0
                    ? ` · ${teamPendingCount} request${teamPendingCount !== 1 ? 's' : ''} need your action`
                    : ' · No pending team requests'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              {teamPendingCount > 0 && (
                <span
                  style={{
                    background: '#f59e0b',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: '800',
                    padding: '6px 12px',
                    borderRadius: '999px',
                    minWidth: '28px',
                    textAlign: 'center',
                  }}
                >
                  {teamPendingCount}
                </span>
              )}
              <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--accent)' }}>Open →</span>
            </div>
          </div>
        </button>
      )}

      {/* CHECK-IN / CHECK-OUT CARD */}
      <div style={{
        background: isCheckedIn && !isCheckedOut
          ? 'linear-gradient(135deg, #1a6db3 0%, #3b5bdb 100%)'
          : isCheckedOut
            ? 'linear-gradient(135deg, #2a9d4f 0%, #1e7e34 100%)'
            : 'linear-gradient(135deg, #2c2c54 0%, #1c2032 100%)',
        borderRadius: '16px',
        padding: '28px 24px',
        marginBottom: '20px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decoration */}
        <div style={{
          position: 'absolute', top: '-30px', right: '-30px',
          width: '180px', height: '180px', borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)'
        }} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
          {/* Left: Status + Time */}
          <div>
            <div style={{ fontSize: '12px', fontWeight: '600', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
              {isCheckedOut ? 'Shift Complete' : isCheckedIn ? 'Currently Working' : 'Not Checked In'}
            </div>

            {/* Live Clock */}
            <div style={{ fontSize: '40px', fontWeight: '800', letterSpacing: '-2px', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
            </div>

            {/* Elapsed / Times */}
            {isCheckedIn && (
              <div style={{ 
                marginTop: '12px', 
                display: 'flex', 
                gap: '20px', 
                flexWrap: 'wrap',
                rowGap: '12px' 
              }}>
                <div>
                  <div style={{ fontSize: '11px', opacity: 0.6, marginBottom: '2px' }}>Check In</div>
                  <div style={{ fontSize: '15px', fontWeight: '700' }}>{formatTime(todaysAttendance.checkIn)}</div>
                </div>
                {isCheckedOut && (
                  <div>
                    <div style={{ fontSize: '11px', opacity: 0.6, marginBottom: '2px' }}>Check Out</div>
                    <div style={{ fontSize: '15px', fontWeight: '700' }}>{formatTime(todaysAttendance.checkOut)}</div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: '11px', opacity: 0.6, marginBottom: '2px' }}>{isCheckedOut ? 'Total Time' : 'Elapsed'}</div>
                  <div style={{ fontSize: '15px', fontWeight: '700', fontVariantNumeric: 'tabular-nums' }}>{elapsed}</div>
                </div>
              </div>
            )}
          </div>

          {/* Right: Button */}
          <div>
            {!isCheckedIn ? (
              <button
                onClick={handleCheckIn}
                disabled={loading}
                style={{
                  background: 'white', color: '#1c2032',
                  border: 'none', borderRadius: '50px',
                  padding: '16px 32px', fontSize: '16px', fontWeight: '800',
                  cursor: 'pointer', letterSpacing: '0.5px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                  display: 'flex', alignItems: 'center', gap: '10px',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  whiteSpace: 'nowrap'
                }}
                onMouseOver={e => e.currentTarget.style.transform = 'scale(1.04)'}
                onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                <MapPin size={22} fill="currentColor" fillOpacity={0.2} /> Check In
              </button>
            ) : !isCheckedOut ? (
              <button
                onClick={handleCheckOut}
                disabled={loading}
                style={{
                  background: 'rgba(255,255,255,0.15)', color: 'white',
                  border: '2px solid rgba(255,255,255,0.5)', borderRadius: '50px',
                  padding: '16px 32px', fontSize: '16px', fontWeight: '800',
                  cursor: 'pointer', letterSpacing: '0.5px',
                  backdropFilter: 'blur(4px)',
                  display: 'flex', alignItems: 'center', gap: '10px',
                  transition: 'transform 0.15s',
                  whiteSpace: 'nowrap'
                }}
                onMouseOver={e => e.currentTarget.style.transform = 'scale(1.04)'}
                onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                <CheckCircle2 size={22} /> Check Out
              </button>
            ) : (
              <div style={{
                background: 'rgba(255,255,255,0.15)', borderRadius: '12px',
                padding: '16px 24px', textAlign: 'center'
              }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                  <PartyPopper size={32} />
                </div>
                <div style={{ fontSize: '13px', fontWeight: '600' }}>Great work today!</div>
                <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '2px' }}>{formatDuration(todaysAttendance.totalHours)} logged</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Table / Summary - Mobile Responsive Grid */}
      <style>{`
        @media (max-width: 600px) {
          .stats-grid { grid-template-columns: 1fr 1fr !important; }
          .quick-links { grid-template-columns: 1fr !important; }
          .checkin-card { padding: 20px 16px !important; }
          .clock-text { font-size: 32px !important; }
        }
      `}</style>

      {/* Stats Row */}
      <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <div className="card" style={{ padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <div className="ic-box gn" style={{ width: '30px', height: '30px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Building2 size={16} /></div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: 'var(--text2)', fontWeight: '600', textTransform: 'uppercase' }}>Dept.</div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--accent)' }}>{currentUser.dept || currentUser.department || '—'}</div>
          </div>
        </div>
        <div className="card" style={{ padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <div className="ic-box am" style={{ width: '30px', height: '30px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Briefcase size={16} /></div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: 'var(--text2)', fontWeight: '600', textTransform: 'uppercase' }}>Leaves Taken</div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--amber)' }}>{approvedLeaves} days</div>
          </div>
        </div>
        <div className="card" style={{ padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <div className="ic-box rd" style={{ width: '30px', height: '30px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Clock size={16} /></div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: 'var(--text2)', fontWeight: '600', textTransform: 'uppercase' }}>My leave pending</div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: pendingLeaves > 0 ? 'var(--red)' : 'var(--green)' }}>
              {pendingLeaves > 0 ? `${pendingLeaves} req` : 'None'}
            </div>
          </div>
        </div>
        <div className="card" style={{ padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <div className="ic-box bl" style={{ width: '30px', height: '30px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ShieldCheck size={16} /></div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: 'var(--text2)', fontWeight: '600', textTransform: 'uppercase' }}>Balance</div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--green)' }}>
              {currentUser.leaveBalance?.casual ?? '—'} CL
            </div>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="quick-links" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
        {headDepartments.length > 0 && (
          <div
            className="card"
            onClick={() => onNavigate && onNavigate('team-leaves')}
            style={{
              padding: '18px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.15s',
              border: teamPendingCount > 0 ? '2px solid #f59e0b' : undefined,
              background: teamPendingCount > 0 ? 'linear-gradient(180deg, #fffbeb 0%, #fff 100%)' : undefined,
              position: 'relative',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = '#d97706';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = teamPendingCount > 0 ? '#f59e0b' : 'var(--border)';
            }}
          >
            {teamPendingCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: '#f59e0b',
                  color: 'white',
                  fontSize: '11px',
                  fontWeight: '800',
                  padding: '2px 8px',
                  borderRadius: '999px',
                }}
              >
                {teamPendingCount}
              </span>
            )}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px', color: teamPendingCount > 0 ? '#b45309' : 'var(--accent)' }}>
              <UserCheck size={28} />
            </div>
            <div style={{ fontSize: '13px', fontWeight: '600' }}>Team Leaves</div>
            <div style={{ fontSize: '11px', color: 'var(--text2)', marginTop: '3px' }}>Approve team requests</div>
          </div>
        )}
        <div
          className="card"
          onClick={() => onNavigate && onNavigate('my-leaves')}
          style={{ padding: '18px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s' }}
          onMouseOver={e => e.currentTarget.style.borderColor = 'var(--accent)'}
          onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}
        >
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px', color: 'var(--accent)' }}>
            <ClipboardList size={28} />
          </div>
          <div style={{ fontSize: '13px', fontWeight: '600' }}>Apply Leave</div>
          <div style={{ fontSize: '11px', color: 'var(--text2)', marginTop: '3px' }}>Request time off</div>
        </div>
        <div
          className="card"
          onClick={() => onNavigate && onNavigate('my-attendance')}
          style={{ padding: '18px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s' }}
          onMouseOver={e => e.currentTarget.style.borderColor = 'var(--accent)'}
          onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}
        >
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px', color: 'var(--green)' }}>
            <CalendarDays size={28} />
          </div>
          <div style={{ fontSize: '13px', fontWeight: '600' }}>Attendance</div>
          <div style={{ fontSize: '11px', color: 'var(--text2)', marginTop: '3px' }}>View history</div>
        </div>
        <div
          className="card"
          onClick={() => onNavigate && onNavigate('notifications')}
          style={{ padding: '18px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s' }}
          onMouseOver={e => e.currentTarget.style.borderColor = 'var(--accent)'}
          onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}
        >
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px', color: 'var(--amber)' }}>
            <Bell size={28} />
          </div>
          <div style={{ fontSize: '13px', fontWeight: '600' }}>Notifications</div>
          <div style={{ fontSize: '11px', color: 'var(--text2)', marginTop: '3px' }}>Updates & alerts</div>
        </div>
      </div>
    </div>
  );
}
